// EnumOptionsField.jsx

import React, { useCallback, useState } from 'react';

// Context
import { useIcons } from 'context/IconsContext';

// Components
import Button from 'components/button/Button';
import ColorButton from 'components/colorbutton/ColorButton';
import IconPickerButton from 'components/iconpicker/IconPickerButton';
import ModalField from 'components/modal/field/ModalField';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';

// Icons
import Icon from 'svg/Icon';

const resolveOptionIconKey = (iconKey, iconsByKey, fallbackIconKey = '') => {
    const normalizedIconKey = typeof iconKey === 'string' ? iconKey.trim() : '';
    if (!normalizedIconKey) return fallbackIconKey;
    if (!iconsByKey) return normalizedIconKey;
    return iconsByKey[normalizedIconKey] ? normalizedIconKey : fallbackIconKey;
};

const EnumOptionsField = ({
    addLabel = 'Add Option',
    emptyCopy = 'No options yet.',
    label = 'Options',
    selectedEnumOptions,
    optionValueDrafts,
    setOptionValueDrafts,
    onOptionValueCommit,
    onOptionValueKeyDown,
    onOptionColorChange,
    onOptionIconChange,
    onOptionMove,
    onOptionRemove,
    onOptionAdd,
    placeholder = 'Internal value',
    showColors = true,
    showIcons = true,
    theme,
    optionsError,
}) => {
    const { iconsByKey } = useIcons();
    const [draggingOptionId, setDraggingOptionId] = useState('');
    const [dragOverOptionId, setDragOverOptionId] = useState('');

    const resetDragState = useCallback(() => {
        setDraggingOptionId('');
        setDragOverOptionId('');
    }, []);

    const handleDragStart = useCallback((event, optionId) => {
        if (!optionId) return;
        setDraggingOptionId(optionId);
        setDragOverOptionId(optionId);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', optionId);
    }, []);

    const handleDragOver = useCallback((event, optionId) => {
        event.preventDefault();
        if (!optionId) return;
        event.dataTransfer.dropEffect = 'move';
        if (dragOverOptionId !== optionId) {
            setDragOverOptionId(optionId);
        }
    }, [dragOverOptionId]);

    const handleDrop = useCallback((event, dropOptionId) => {
        event.preventDefault();

        const transferredId = event.dataTransfer.getData('text/plain');
        const fromOptionId = String(transferredId || draggingOptionId || '').trim();
        const toOptionId = String(dropOptionId || '').trim();

        if (fromOptionId && toOptionId && fromOptionId !== toOptionId) {
            onOptionMove(fromOptionId, toOptionId);
        }

        resetDragState();
    }, [draggingOptionId, onOptionMove, resetDragState]);

    return (

        <ModalField>

            <ModalLabel label={label} />
            <ModalHint hint="List of choices for the field." />

            {selectedEnumOptions.length === 0 ? (
                <div
                    className="field-modal-empty"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    {emptyCopy}
                </div>
            ) : (
                <div className="field-modal-options">
                    {selectedEnumOptions.map((option) => {
                        const optionId = option?.id || option?.value || '';
                        const optionValue = option?.value ?? '';
                        const hasDraft = optionValueDrafts
                            && Object.prototype.hasOwnProperty.call(optionValueDrafts, optionId);
                        const draftValue = hasDraft ? optionValueDrafts[optionId] : optionValue;
                        const pickerIconKey = resolveOptionIconKey(option?.iconKey, iconsByKey, 'category');

                        return (
                            <div
                                className={`field-modal-option${draggingOptionId === optionId ? ' is-dragging' : ''}${dragOverOptionId === optionId && draggingOptionId !== optionId ? ' is-drop-target' : ''}`}
                                key={optionId || optionValue}
                                onDragOver={(event) => handleDragOver(event, optionId)}
                                onDrop={(event) => handleDrop(event, optionId)}
                            >
                                <div
                                    className="field-modal-option-handle"
                                    draggable
                                    onDragStart={(event) => handleDragStart(event, optionId)}
                                    onDragEnd={resetDragState}
                                    title="Drag to reorder"
                                >
                                    <Icon
                                        iconKey="drag_indicator"
                                        width={18}
                                        height={18}
                                        color={theme.foregroundMuted}
                                    />
                                </div>
                                {showIcons && typeof onOptionIconChange === 'function' ? (
                                    <IconPickerButton
                                        className="field-modal-option-icon-trigger"
                                        backgroundColor={theme.backgroundMuted}
                                        borderRadius={8}
                                        buttonSize={30}
                                        hoverBackgroundColor={theme.backgroundHover}
                                        iconColor={theme.foregroundMuted}
                                        iconSize={18}
                                        popupHeight={360}
                                        popupWidth={360}
                                        selectedIconKey={pickerIconKey}
                                        title="Choose option icon"
                                        onClear={({ closePicker } = {}) => {
                                            onOptionIconChange(optionId, '');
                                            closePicker?.();
                                        }}
                                        onSelect={(nextIconKey, { closePicker } = {}) => {
                                            onOptionIconChange(optionId, nextIconKey);
                                            closePicker?.();
                                        }}
                                    />
                                ) : null}
                                <TextInput
                                    value={draftValue}
                                    onChange={(e) => {
                                        const nextValue = e.target.value;
                                        setOptionValueDrafts((prev) => ({
                                            ...(prev || {}),
                                            [optionId]: nextValue,
                                        }));
                                    }}
                                    onBlur={() => onOptionValueCommit(optionId)}
                                    onKeyDown={(e) => onOptionValueKeyDown(optionId, e)}
                                    placeholder={placeholder}
                                />
                                {showColors ? (
                                    <ColorButton
                                        color={option?.color}
                                        onChange={(nextColor) => onOptionColorChange(optionId, nextColor)}
                                    />
                                ) : null}
                                <div
                                    className="field-modal-option-remove"
                                    onClick={() => onOptionRemove(optionId)}
                                >
                                    <Icon
                                        iconKey="do_not_disturb_on"
                                        width={20}
                                        height={20}
                                        color={theme.foregroundMuted}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {optionsError && (
                <div className="field-modal-error">{optionsError}</div>
            )}
            <div className="field-modal-options-toolbar">
                <Button
                    label={addLabel}
                    onClick={onOptionAdd}
                    height="38px"
                    borderRadius="9px"
                    enabled
                />
            </div>

        </ModalField>

    );
};

export default EnumOptionsField;
